<template>
  <div>
    <div class="image-list">
      <div v-for="(file, index) in fileList" :key="file.uid" class="image-item">
        <img
          :src="toDisplayUrl(file.url || file.thumbUrl)"
          :style="{ width: `${width}px`, height: `${height}px` }"
          alt="image"
        />
        <a-button type="link" @click="handleRemove(index)">删除</a-button>
      </div>
    </div>
    <a-upload
      :fileList="fileList"
      list-type="picture-card"
      :multiple="num > 1"
      :accept="accept"
      :beforeUpload="beforeUpload"
      :customRequest="customRequest"
      :showUploadList="false"
      @change="handleChange"
    >
      <div v-if="fileList.length < num">
        <a-button>
          <a-icon type="upload" />
          上传图片
        </a-button>
      </div>
    </a-upload>
  </div>
</template>

<script>
import { uploadFileDirectToOss } from "@/utils/directOssUpload";

export default {
  name: "ImageUpload",
  props: {
    value: {
      type: String,
      default: "",
    },
    num: {
      type: Number,
      default: 1,
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: 100,
    },
    maxSizeMb: {
      type: Number,
      default: 10,
    },
    accept: {
      type: String,
      default: ".png,.jpg,.jpeg",
    },
    allowedExtensions: {
      type: Array,
      default: () => ["png", "jpg", "jpeg"],
    },
    activityId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      fileList: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.fileList = [];
          return;
        }
        const urls = String(newVal)
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
        this.fileList = urls.map((url, index) => ({
          uid: `-${index}`,
          name: `image-${index}.jpg`,
          status: "done",
          url,
        }));
      },
    },
  },
  methods: {
    resolveUploadUrl(response) {
      const payload = response && response.data ? response.data : response;
      if (!payload) return "";
      if (typeof payload === "string") return payload.trim();
      if (typeof payload.link === "string" && payload.link.trim()) return payload.link.trim();
      if (typeof payload.url === "string" && payload.url.trim()) return payload.url.trim();
      if (payload.data && typeof payload.data === "object") {
        if (typeof payload.data.link === "string" && payload.data.link.trim()) {
          return payload.data.link.trim();
        }
        if (typeof payload.data.url === "string" && payload.data.url.trim()) {
          return payload.data.url.trim();
        }
      }
      return "";
    },
    toDisplayUrl(url) {
      return String(url || "").trim();
    },
    beforeUpload(file) {
      const isImage = file.type ? file.type.startsWith("image/") : true;
      const extension = String(file.name || "")
        .split(".")
        .pop()
        .trim()
        .toLowerCase();
      const isAllowedExtension = !this.allowedExtensions.length || this.allowedExtensions.includes(extension);
      if (file.size / 1024 / 1024 > this.maxSizeMb) {
        this.$message.error(`图片大小不能超过 ${this.maxSizeMb}MB`);
        return false;
      }
      if (!isImage) {
        this.$message.error("只能上传图片文件");
        return false;
      }
      if (!isAllowedExtension) {
        this.$message.error(`仅支持上传 ${this.allowedExtensions.join("/")} 格式的图片`);
        return false;
      }
      if (this.fileList.length >= this.num) {
        this.$message.error(`最多只能上传 ${this.num} 张图片`);
        return false;
      }
      return true;
    },
    async customRequest(options) {
      const { file, onProgress, onSuccess, onError } = options;
      try {
        const result = await uploadFileDirectToOss(file, {
          onProgress,
          activityId: this.activityId,
        });
        const imageUrl = this.resolveUploadUrl(result);
        this.fileList = [
          ...this.fileList,
          {
            uid: file.uid || `${Date.now()}`,
            name: file.name,
            status: "done",
            url: imageUrl,
          },
        ];
        this.emitValue();
        if (typeof onProgress === "function") {
          onProgress({ percent: 100 });
        }
        if (typeof onSuccess === "function") {
          onSuccess({ url: imageUrl }, file);
        }
      } catch (error) {
        this.$message.error(
          (error && error.response && error.response.data && error.response.data.msg) ||
            error.message ||
            "图片上传失败"
        );
        if (typeof onError === "function") {
          onError(error);
        }
      }
    },
    handleChange(info) {
      if (info.file && info.file.status === "error") {
        this.$message.error("图片上传失败");
      }
    },
    handleRemove(index) {
      this.fileList.splice(index, 1);
      this.emitValue();
    },
    emitValue() {
      const imageUrls = this.fileList.map((file) => file.url).join(",");
      this.$emit("input", imageUrls);
    },
  },
};
</script>

<style scoped>
.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.image-item {
  position: relative;
  border: 1px solid #ddd;
  padding: 5px;
}

.image-item img {
  display: block;
  object-fit: cover;
}

.image-item button {
  position: absolute;
  bottom: 5px;
  right: 5px;
}
</style>
